import { DateTime, Duration } from 'luxon'
import { ProbabilityModel } from './probability-model'

export class Distribution {
  constructor(timeZone, startDate, missionTime, distributionParameters) {
    this.timeZone = timeZone
    this.startDate = startDate
    this.missionTime = missionTime
    this.distributionParameters = distributionParameters
  }

  get startDate() {
    return this._startDate
  }

  set startDate(value) {
    this._startDate = value
      ? DateTime.isDateTime(value)
        ? value
        : DateTime.fromISO(value, { zone: this.timeZone })
      : null
  }

  get missionTime() {
    return this._missionTime
  }

  set missionTime(value) {
    this._missionTime = value ? (Duration.isDuration(value) ? value : Duration.fromISO(value)) : null
  }

  get missionDays() {
    if (!this.missionTime || !this.startDate || !this.endDate) {
      return null
    }
    let diff = this.endDate.diff(this.startDate, 'days')
    return Math.ceil(diff.days)
  }

  get endDate() {
    if (!this.startDate || !this.missionTime) {
      return null
    }

    let end = this.startDate.plus(this.missionTime)
    return end.isValid ? end : null
  }

  validate() {
    return !!(this.startDate && this.missionTime && this.missionTime.as('days') >= 0)
  }
}

export class Weibull extends Distribution {
  constructor(timeZone, startDate, missionTime, distributionParameters) {
    super(timeZone, startDate, missionTime, distributionParameters)

    this.type = !distributionParameters || !distributionParameters.type ? null : distributionParameters.type
    this.shape =
      !distributionParameters || distributionParameters.shape === null || distributionParameters.shape === undefined
        ? null
        : Number(distributionParameters.shape)
    this.scale =
      !distributionParameters || distributionParameters.scale === null || distributionParameters.scale === undefined
        ? null
        : Duration.fromISO(distributionParameters.scale).as('days')
  }

  get parameters() {
    return this.distributionParameters
  }

  set parameters(value) {
    this.distributionParameters = value

    this.type = !value || !value.type ? null : value.type
    this.shape = !value || value.shape === null || value.shape === undefined ? null : Number(value.shape)
    this.scale =
      !value || value.scale === null || value.scale === undefined ? null : Duration.fromISO(value.scale).as('days')
  }

  calculateRiskAtMissionDate() {
    if (!this.validate()) return null

    return this.type === 'pdf' ? this.pdf(this.missionDays) : this.cdf(this.missionDays)
  }

  calculate(duration) {
    let points = []
    if (!this.validate()) {
      this.riskAtMissionDate = null
      return points
    }

    let daysToCalc = this.missionDays
    if (duration) {
      let alternateEndDate = this.startDate.plus(Duration.fromISO(duration))
      let diff = alternateEndDate.diff(this.startDate, 'days')
      daysToCalc = Math.ceil(diff.days)
    }

    for (let i = 0; i <= daysToCalc; i++) {
      points.push([i, this.type === 'pdf' ? this.pdf(i) : this.cdf(i)])
    }

    this.riskAtMissionDate = points.length > this.missionDays ? points[this.missionDays][1] : null
    return points
  }

  pdf(t) {
    return t === null || t === undefined || t < 0 || this.scale < 0 || this.shape < 0
      ? null
      : (this.shape / this.scale) *
          Math.pow(t / this.scale, this.shape - 1) *
          Math.exp(-Math.pow(t / this.scale, this.shape))
  }

  cdf(t) {
    return t === null || t === undefined || t < 0 || this.scale < 0 || this.shape < 0
      ? null
      : 1 - Math.exp(-Math.pow(t / this.scale, this.shape))
  }

  validate() {
    return !super.validate() ||
      !this.type ||
      this.shape === null ||
      this.shape === undefined ||
      this.shape < 0 ||
      this.scale === null ||
      this.scale === undefined ||
      this.scale < 0 ||
      (this.type !== 'pdf' && this.type !== 'cdf')
      ? false
      : true
  }
}

export class NoDistribution extends Distribution {
  constructor(timeZone, startDate, missionTime, pof) {
    super(timeZone, startDate, missionTime, null)

    this.pof = parseInt(pof)
  }

  calculateMtbfAtMissionDate() {
    if (this.pof === 0) {
      //  If probability is 0, then scale is essentially infinity, so return the biggest period supported.
      return Duration.fromObject({ years: 2147483647 }).toISO()
    }

    let days = this.inverseCdf(this.missionDays)
    if (days === null) {
      return null
    }

    let daysRemainder = days % 1
    let truncatedHours = Math.floor(daysRemainder * 24)

    let mtbf = Duration.fromObject({ days: Math.floor(days), hours: truncatedHours })
    return mtbf.toISO()
  }

  inverseCdf(t) {
    if (!this.validate()) {
      return null
    }

    //  We assume shape is 1 which simplifies the formula. If we want to support a variable shape,
    //  the formula would be t / Math.pow(-Math.log(1 - (this.pof / 100)), 1 / <shape>)
    return t / -Math.log(1 - this.pof / 100)
  }

  validate() {
    return super.validate() && this.pof !== null && this.pof !== undefined && this.pof > 0 && !Number.isNaN(this.pof)
  }
}

export class ProbabilityCalculator {
  static calculate(timeZone, startDate, missionTime, probability, analysis) {
    if (!probability) {
      throw new Error('No probability record.')
    }
    if (!(probability instanceof ProbabilityModel)) {
      throw new Error('Must be type ProbabilityModel.')
    }

    let distributionCalc = null

    if (probability.failureDistribution === 'weibull') {
      distributionCalc = new Weibull(timeZone, startDate, missionTime, probability.failureDistributionParameters)
    } else if (probability.failureDistribution === 'none') {
      if (
        probability.failureDistributionParameters.scale === null ||
        probability.failureDistributionParameters.scale === undefined
      ) {
        const noneDistributionCalc = new NoDistribution(timeZone, startDate, missionTime, probability.value)
        probability.failureDistributionParameters.scale = noneDistributionCalc.calculateMtbfAtMissionDate()
      } else {
        distributionCalc = new Weibull(timeZone, startDate, missionTime, {
          type: 'cdf',
          shape: 1,
          scale: probability.failureDistributionParameters.scale
        })
      }
    }
    if (distributionCalc) {
      let answer = distributionCalc.calculateRiskAtMissionDate()
      probability.value = answer !== null ? Math.trunc(answer * 100) : null
    } else {
      probability.value = null
    }

    if (analysis) {
      const calcStart = startDate || analysis.startDate
      const calcMission = startDate
        ? ProbabilityCalculator.calculateMissionTime(startDate, analysis.endDate, timeZone)
        : analysis.missionTime
      const calcDistributionParameters =
        probability.failureDistribution === 'weibull'
          ? probability.failureDistributionParameters
          : { type: 'cdf', shape: 1, scale: probability.failureDistributionParameters.scale }
      const analysisDistributionCalc = new Weibull(timeZone, calcStart, calcMission, calcDistributionParameters)
      let answer = analysisDistributionCalc.calculateRiskAtMissionDate()
      probability.riskAnalysisValue = answer !== null ? Math.trunc(answer * 100) : null
    }
  }

  static calculateMissionTime(startDate, endDate, timeZone) {
    if (!startDate || !endDate || !timeZone) {
      return null
    }
    const start = DateTime.fromISO(startDate, { zone: timeZone })
    const end = DateTime.fromISO(endDate, { zone: timeZone })

    //  Always calculate mission time in days and then truncate to whole day so that the
    //  answer matches the last point of the graph (and the server)
    let calcMission = end.diff(start, ['days'])
    calcMission = calcMission.set({ days: Math.trunc(calcMission.days) })
    return calcMission.toISO()
  }
}

export default { Distribution, Weibull }
